"use client";
import { signOut } from "next-auth/react";
import Link from "next/link";
import { useTheme } from "next-themes";
import { useState } from "react";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { useTranslations } from "next-intl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export function ProfileMemu({
  user,
  onLogout,
}: {
  user?: { name?: string | null; email?: string | null; image?: string | null } | null;
  onLogout?: () => void;
}) {
  const theme = useTheme();
  const t = useTranslations("header");
  const pathname = usePathname();
  const locale = pathname?.split("/")[1] || "zh";

  // Prefer backend-provided `nickname`, fall back to `name`, then email local-part
  const nicknameField = (user && (user as Record<string, unknown>)?.["nickname"]) as
    | string
    | undefined;
  const displayName =
    nicknameField || user?.name || (user?.email ? user.email.split("@")[0] : t("user.defaultName") || "用户");
  const displayEmail = user?.email || "";

  const initials = ((): string => {
    try {
      const s = String(displayName || "").trim();
      if (!s) return "__";
      // take first two characters and uppercase
      return s.slice(0, 2).toUpperCase();
    } catch {
      return "__";
    }
  })();

  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);

    try {
      await signOut({ redirect: false });
    } catch {
      // ignore
    }

    onLogout?.();

    // Immediately redirect the user to login
    window.location.href = `/${locale}/login`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="cursor-pointer h-10 aspect-square rounded-full hover:bg-muted transition-colors"
        >
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-black text-white font-bold text-sm">{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64 p-2" align="end" forceMount>
        <DropdownMenuLabel className="font-normal p-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-black text-white font-bold text-base">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{displayName}</p>
              <p className="text-xs leading-none text-muted-foreground">{displayEmail}</p>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-2" />
        <DropdownMenuItem asChild>
          <Link
            href={`/${locale}/pim`}
            className="p-3 cursor-pointer hover:bg-muted rounded-md transition-colors"
          >
            <span className="flex items-center gap-2">{t("user.profile") || "个人信息"}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            href={`/${locale}/pim/notify`}
            className="p-3 cursor-pointer hover:bg-muted rounded-md transition-colors"
          >
            <span className="flex items-center gap-2">{t("user.notifications") || "通知设置"}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            href={`/${locale}/pim/recharge`}
            className="p-3 cursor-pointer hover:bg-muted rounded-md transition-colors"
          >
            <span className="flex items-center gap-2">{t("user.topup") || "余额充值"}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-2" />
        <DropdownMenuItem
          className="p-3 cursor-pointer text-destructive hover:bg-destructive/10 hover:text-destructive rounded-md transition-colors"
          onClick={handleLogout}
          aria-disabled={loggingOut}
        >
          <span className="flex items-center gap-2">{loggingOut ? t("alerts.loggingOut") : t("nav.logout")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
